import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useState } from 'react';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  AveragePlayerScore,
  ScoreStatus,
} from '@sorare/core/src/__generated__/globalTypes';
import { Cards } from '@sorare/core/src/assets/icons/Cards';
import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { Switch } from '@sorare/core/src/atoms/inputs/Switch';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import {
  BodyS,
  BrandXXS,
  LabelL,
  LabelS,
  LabelXS,
} from '@sorare/core/src/atoms/typography';
import { Ellipsis } from '@sorare/core/src/atoms/typography/Ellipsis';
import { Alert } from '@sorare/core/src/atoms/ui/Alert';
import { Card } from '@sorare/core/src/components/card/Card';
import { CardFooter } from '@sorare/core/src/components/card/CardFooter';
import { DropCardsWarningDialog } from '@sorare/core/src/components/commonGame/DropCardsWarningDialog';
import { DropRank } from '@sorare/core/src/components/commonGame/DropRank';
import { Score } from '@sorare/core/src/components/scoring/Score';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { range } from '@sorare/core/src/lib/arrays';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import {
  cardAttributes,
  glossary,
  navLabels,
} from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { CardBonus } from '@sorare/marketplace/src/components/card/CardBonus';
import { RevealPackCardsDialogFromReward } from '@sorare/marketplace/src/components/commonCard/RevealPackCardsDialogFromReward';
import { AnyPlayerStatsIcon } from '@sorare/marketplace/src/components/player/AnyPlayerStatsIcon';
import { BasicBenchExtraWrapper } from '@sorare/marketplace/src/components/searchPlayerTable/BasicBenchExtraWrapper';

import { BasicBenchCard } from '#src/components/compose/BasicBenchCard/index.tsx';
import { DropChallengeEmptyState } from '#src/components/missions/DailyTask/DropChallengeButton/DropChallengeEmptyState/index.tsx';
import { DropChallengePickerOnboardingDialog } from '#src/components/missions/DailyTask/DropChallengeButton/DropChallengePickerOnboardingDialog/index.tsx';
import { TaskRules } from '#src/components/missions/TaskRules/index.tsx';
import { BaseSlot } from '#src/components/missions/picker/BaseSlot/index.tsx';
import { TaskCardsPicker } from '#src/components/missions/picker/TaskCardsPicker/index.tsx';
import { TaskPickerDialogLayout } from '#src/components/missions/picker/TaskPickerDialogLayout/index.tsx';

import type {
  DropChallengePickerDialogQuery,
  DropChallengePickerDialogQueryVariables,
  DropChallengePickerDialog_dropChallengeTask,
  completeDropChallengeMutation,
  completeDropChallengeMutationVariables,
} from './__generated__/index.graphql.ts';

const StyledSkeleton = styled(Skeleton)`
  aspect-ratio: var(--card-aspect-ratio);
`;

const DROP_CHALLENGE_PICKER_QUERY = gql`
  query DropChallengePickerDialogQuery(
    $taskId: ID!
    $after: String
    $averageScoreType: AveragePlayerScore!
    $onlyDuplicate: Boolean!
    $so5LeaderboardSlug: String
    $gameId: ID
  ) {
    currentUser {
      slug
      task(id: $taskId) {
        id
        ... on DropChallengeTask {
          id
          displayedTypedRules {
            id
            key
            ...TaskRules_so5RuleInterface
          }
          availableCards(after: $after, onlyDuplicate: $onlyDuplicate) {
            nodes {
              slug
              averageScore(type: $averageScoreType)
              dropRank
              openedSo5Lineups {
                id
              }
              liveSo5Lineup {
                id
              }
              anyPlayer {
                slug
                ...AnyPlayerStatsIcon_anyPlayer
              }
              ...BaseSlot_anyCardInterface
              ...Card_anyCard
              ...DropCardsWarningDialog_anyCardInterface
              ...TaskCardsPicker_anyCardInterface
              ...CardBonus_anyCard
            }
            pageInfo {
              hasNextPage
              endCursor
            }
            totalCount
          }
        }
      }
    }
  }
  ${BaseSlot.fragments.anyCardInterface}
  ${TaskRules.fragments.so5RuleInterface}
  ${Card.fragments.anyCard}
  ${AnyPlayerStatsIcon.fragments.anyPlayer}
  ${DropCardsWarningDialog.fragments.anyCardInterface}
  ${TaskCardsPicker.fragments.anyCardInterface}
  ${CardBonus.fragments.anyCard}
` as TypedDocumentNode<
  DropChallengePickerDialogQuery,
  DropChallengePickerDialogQueryVariables
>;

type Card = Extract<
  NonNullable<DropChallengePickerDialogQuery['currentUser']>['task'],
  { __typename: 'DropChallengeTask' }
>['availableCards']['nodes'][number] & {
  isUsedInLineup: boolean;
};

const COMPLETE_DROP_CHALLENGE_MUTATION = gql`
  mutation completeDropChallengeMutation($input: completeDropChallengeInput!) {
    completeDropChallenge(input: $input) {
      task {
        id
        aasmState
      }
      rewards {
        id
        ...RevealPackCardsDialogFromReward_anyRewardInterface
      }
      errors {
        message
        code
      }
    }
  }
  ${RevealPackCardsDialogFromReward.fragments.anyRewardInterface}
` as TypedDocumentNode<
  completeDropChallengeMutation,
  completeDropChallengeMutationVariables
>;

type Props = {
  onClose: () => void;
  task: DropChallengePickerDialog_dropChallengeTask;
  onSuccess: () => void;
};
export const DropChallengePickerDialog = ({
  onClose,
  task,
  onSuccess,
}: Props) => {
  const { formatMessage } = useIntl();
  const { lifecycle, update } = useLifecycle();
  const [openRewardDialog, setOpenRewardDialog] = useState(false);
  const [onlyDuplicate, setOnlyDuplicate] = useState(false);
  const [claimMutationData, setClaimMutationData] =
    useState<completeDropChallengeMutation['completeDropChallenge']>();
  const track = useEvents();
  const { data, loading, loadMore } = usePaginatedQuery(
    DROP_CHALLENGE_PICKER_QUERY,
    {
      variables: {
        taskId: idFromObject(task.id),
        averageScoreType: AveragePlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
        onlyDuplicate,
      },
      connection: 'AnyCardInterfaceConnection',
    }
  );
  const [mutate, { loading: mutationLoading }] = useMutation(
    COMPLETE_DROP_CHALLENGE_MUTATION,
    {
      showErrorsWithSnackNotification: true,
    }
  );

  const onConfirm = async (cardSlugs: string[]) => {
    const { data: dataFromMutation } = await mutate({
      variables: {
        input: { cardSlugs, taskId: task.id },
      },
    });
    setClaimMutationData(dataFromMutation?.completeDropChallenge);
    setOpenRewardDialog(true);
  };

  const { cardsCount } = task;
  const taskFromQuery = data?.currentUser?.task;
  const dropChallengeTask = isType(taskFromQuery, 'DropChallengeTask')
    ? taskFromQuery
    : null;
  const { availableCards, displayedTypedRules } = dropChallengeTask || {};
  const { nodes, pageInfo } = availableCards || {};

  useEffect(() => {
    if (typeof availableCards?.totalCount === 'number') {
      track('View Scrap Screen', {
        cardsCount: availableCards?.totalCount,
      });
    }
  }, [track, availableCards?.totalCount]);

  const { InfiniteScrollLoader } = useInfiniteScroll(
    () => {
      loadMore(false, {
        after: pageInfo?.endCursor,
      });
    },
    Boolean(pageInfo?.hasNextPage),
    loading
  );

  if (!cardsCount) {
    return null;
  }

  const showEmptyState = !loading && (nodes?.length || 0) < cardsCount;

  const shouldShowOnboarding = !lifecycle?.sawDropChallengePickerOnboarding;

  if (shouldShowOnboarding) {
    return (
      <DropChallengePickerOnboardingDialog
        onClose={() => {
          update(LIFECYCLE.sawDropChallengePickerOnboarding, true);
        }}
        cardsCount={cardsCount}
      />
    );
  }

  return (
    <>
      <TaskPickerDialogLayout
        task={task}
        leftColumnChildren={
          <Vertical gap={3}>
            <TaskRules displayedTypedRules={displayedTypedRules || []} />
            <Vertical>
              <Alert
                title={
                  <LabelL bold as="span">
                    <FormattedMessage {...glossary.warning} />
                  </LabelL>
                }
                message={
                  <BodyS>
                    <FormattedMessage defaultMessage="You will not receive Essence for this scrap" />
                  </BodyS>
                }
              />
            </Vertical>
          </Vertical>
        }
        onClose={onClose}
      >
        <TaskCardsPicker<Card, Card>
          title={formatMessage(navLabels.myCards)}
          defaultSlots={new Array(cardsCount).fill({})}
          defaultSelectedSlotIndex={0}
          cards={
            nodes?.map(node => {
              return {
                ...node,
                isUsedInLineup:
                  node.openedSo5Lineups.length > 0 ||
                  node.liveSo5Lineup !== null,
              };
            }) || []
          }
          clickToUnselect
          extra={
            <Horizontal>
              <Horizontal gap={0.5}>
                <Cards size={1.5} />
                <LabelS>
                  <FormattedMessage defaultMessage="Duplicate only" />
                </LabelS>
              </Horizontal>
              <Switch
                checked={onlyDuplicate}
                onChange={() => setOnlyDuplicate(!onlyDuplicate)}
              />
            </Horizontal>
          }
          renderButton={slots => {
            const cardSlugs = slots.filter(slot => slot.slug);
            const allPlayersSelected = cardSlugs.length === cardsCount;
            return (
              <LoadingButton
                size="medium"
                loading={mutationLoading}
                disabled={!allPlayersSelected}
                onClick={() => {
                  onConfirm(cardSlugs.map(slot => slot.slug).filter(Boolean));
                }}
              >
                <FormattedMessage {...glossary.confirm} />
              </LoadingButton>
            );
          }}
          renderCard={({ card }) => {
            const { anyPlayer, averageScore, dropRank, isUsedInLineup } = card;
            return (
              <BasicBenchCard
                maskImageSrc={card.pictureUrl}
                footer={
                  <CardFooter
                    left={
                      <AnyPlayerStatsIcon
                        backgroundColor="transparent"
                        player={anyPlayer}
                      />
                    }
                    right={<CardBonus card={card} size="large" />}
                    extra={
                      <>
                        <BasicBenchExtraWrapper height={4}>
                          <DropRank dropRank={dropRank ?? 5} />
                        </BasicBenchExtraWrapper>
                        {!!isUsedInLineup && (
                          <BasicBenchExtraWrapper>
                            <LabelXS>
                              <Ellipsis>
                                <FormattedMessage
                                  {...cardAttributes.usedInLineup}
                                />
                              </Ellipsis>
                            </LabelXS>
                          </BasicBenchExtraWrapper>
                        )}
                      </>
                    }
                  >
                    <Tooltip
                      title={<FormattedMessage {...cardAttributes.last15} />}
                    >
                      <Score
                        hideReviewing
                        shape="HEXAGON"
                        score={averageScore || 0}
                        scoreType="PLAYER_SCORE"
                        scoreStatus={ScoreStatus.FINAL}
                      />
                    </Tooltip>
                  </CardFooter>
                }
              >
                <Card card={card} />
              </BasicBenchCard>
            );
          }}
          renderSlotContent={({ isSelected, card, index }) => {
            return (
              <BaseSlot
                card={card}
                color={isSelected ? 'var(--c-blue-400)' : 'var(--c-nd-150)'}
                icon={
                  <BrandXXS bold>
                    <FormattedNumber value={index + 1} />
                  </BrandXXS>
                }
              />
            );
          }}
          renderWarningDialog={({ card, onCancel, onContinue }) => {
            return (
              <DropCardsWarningDialog
                open
                onCancel={onCancel}
                onContinue={onContinue}
                cards={[card]}
                loading={false}
              />
            );
          }}
        >
          {!!loading && range(20).map(i => <StyledSkeleton key={i} />)}
          {!!showEmptyState && (
            <DropChallengeEmptyState empty={nodes?.length === 0} />
          )}
          <InfiniteScrollLoader />
        </TaskCardsPicker>
      </TaskPickerDialogLayout>
      <RevealPackCardsDialogFromReward
        open={openRewardDialog}
        onClose={() => {
          setOpenRewardDialog(false);
          onSuccess();
          onClose();
        }}
        reward={claimMutationData?.rewards?.at(0)}
      />
    </>
  );
};

DropChallengePickerDialog.fragments = {
  dropChallengeTask: gql`
    fragment DropChallengePickerDialog_dropChallengeTask on DropChallengeTask {
      id
      cardsCount
      ...TaskPickerDialogLayout_task
    }
    ${TaskPickerDialogLayout.fragments.task}
  ` as TypedDocumentNode<DropChallengePickerDialog_dropChallengeTask>,
};
